import React from "react";
import { Home, Building2, Trees, ClipboardCheck, Check, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { PageHero } from "./PageHero";
import { mockData, media } from "../mock";

const getIcon = (title) => {
  if (title.includes("Residential")) return <Home className="h-8 w-8" />;
  if (title.includes("Commercial")) return <Building2 className="h-8 w-8" />;
  if (title.includes("Site")) return <Trees className="h-8 w-8" />;
  return <ClipboardCheck className="h-8 w-8" />;
};

const areas = [
  { tag: "Primary", title: "DMV Area", desc: "Washington DC, Maryland, and Virginia — our primary service area with rapid response times." },
  { tag: "Regional", title: "Mid-Atlantic", desc: "Extended coverage throughout the Mid-Atlantic region for larger commercial projects." },
  { tag: "National", title: "Nationwide", desc: "Specialized demolition projects across the United States for unique and complex operations." },
];

export const ServicesPage = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="services-page">
      <PageHero
        kicker="What We Do"
        title="Professional"
        highlight="Demolition Services"
        subtitle="From residential homes to complex industrial sites, we deliver comprehensive demolition and site work with the highest safety standards — across the DMV and nationwide."
        bg={media.servicesImg}
      />

      {/* Services */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 tex-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border">
            {mockData.services.map((service, idx) => (
              <div key={service.id} data-testid={`services-page-card-${service.id}`} className="group bg-card p-8 lg:p-10 hover:bg-surface-alt transition-colors">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 flex items-center justify-center border border-primary/30 bg-primary/10 text-primary group-hover:bg-primary group-hover:text-background transition-colors">{getIcon(service.title)}</div>
                  <span className="font-display text-6xl font-black text-white/5 group-hover:text-primary/20 transition-colors">0{idx + 1}</span>
                </div>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white mb-3">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-6">{service.description}</p>
                <h4 className="label-kicker mb-3">What&apos;s Included</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {service.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service areas */}
      <section className="tex-plate py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="label-kicker mb-4 text-center">Coverage</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-12 text-center">
            Service Areas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
            {areas.map((a) => (
              <div key={a.title} className="bg-card p-8 text-center">
                <div className="label-kicker mb-3">{a.tag}</div>
                <h3 className="font-display text-3xl font-bold uppercase tracking-tight text-white mb-3">{a.title}</h3>
                <p className="text-gray-400 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 border-t border-border overflow-hidden">
        <div className="absolute inset-0 hazard-stripe opacity-[0.04]" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">Ready To Start Your Project?</h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">Get a free consultation and quote with military precision and uncompromising safety.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="services-page-quote-btn" size="lg" onClick={() => navigate("/quote")} className="rounded-none font-bold uppercase tracking-widest px-8 py-6">
              Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button data-testid="services-page-contact-btn" size="lg" variant="outline" onClick={() => navigate("/contact")} className="rounded-none font-bold uppercase tracking-widest px-8 py-6 border-white/30 bg-transparent text-white hover:bg-white hover:text-background">
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
